import { Box } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { type SystemChipItem, SystemChips } from "../../../UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { SheetStepSectionLabel } from "../../../UIToolkit/Popups/SheetStepSectionLabel";
import { formatStaffCount, shortfallOf } from "../gapsDayModel";
import type { GapsScopedShiftGroup } from "../gapsGroupSheets";
import { useGapsCountsHidden } from "../useGapsCountsHidden";

type Props = {
	/** The day's short groups a post can be prefilled from. */
	shortGroups: readonly GapsScopedShiftGroup[];
	/** `Thu 1 Oct` — the day the post is for. */
	dayLabel: string;
	onPrefillFrom: (groupKey: string) => void;
	testIdPrefix: string;
};

/** How many short shifts a blank post offers to prefill from — the worst few, not the whole day. */
const PREFILL_SUGGESTIONS = 4;

/**
 * A blank post's "short on this day" chips (P3-9): the day's worst few short shifts, the shortest-handed first, each
 * one tap from a post prefilled from that group. Draws nothing when the day has none. With Hide gaps on, the chips
 * keep their order but leave out the count.
 */
export const GapsPostPrefillChips: FC<Props> = ({ shortGroups, dayLabel, onPrefillFrom, testIdPrefix }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { areCountsHidden } = useGapsCountsHidden();
	const suggestionChips = useMemo<SystemChipItem<string>[]>(
		() =>
			[...shortGroups]
				.sort((a, b) => shortfallOf(b) - shortfallOf(a))
				.slice(0, PREFILL_SUGGESTIONS)
				.map((candidate) => ({
					value: candidate.key,
					label: areCountsHidden
						? t("gaps.postSheet.prefillChipCountsHidden", { job: candidate.jobLabel, time: candidate.timeLabel })
						: t("gaps.postSheet.prefillChip", { job: candidate.jobLabel, time: candidate.timeLabel, short: formatStaffCount(shortfallOf(candidate)) }),
				})),
		[shortGroups, areCountsHidden, t],
	);

	if (suggestionChips.length === 0) return null;
	return (
		<Box data-testid={`${testIdPrefix}-prefill`}>
			<SheetStepSectionLabel>{t("gaps.postSheet.prefillLabel", { day: dayLabel })}</SheetStepSectionLabel>
			<Box sx={{ px: 2, pt: 1.25 }}>
				<SystemChips<string>
					items={suggestionChips}
					selectedItems={[]}
					onChange={(item) => {
						track("gaps_post_shift_prefill_picked", { department_id: chosenDepartment.department_id, group: item.value });
						onPrefillFrom(item.value);
					}}
					touchTarget
					forceFitContent
					getItemDataTestId={(item) => `${testIdPrefix}-prefill-${item.value}`}
					rootSx={{ justifyContent: "flex-start" }}
				/>
			</Box>
		</Box>
	);
};
